import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, useMap, useMapEvents } from 'react-leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { FleetClusterLayer } from './FleetClusterLayer';
import { HomeBaseMarker } from './HomeBaseMarker';
import { ShortestPathLayer } from './ShortestPathLayer';
import { useTheme } from '../../hooks/useTheme';
import { DeviceLocationCenter } from './DeviceLocationCenter';
import { MapSearchField } from './MapSearchField';
import { useInitialMapView } from '../../hooks/useInitialMapView';
import { MapViewMemory } from './MapViewMemory';
import { FleetViewport } from './FleetViewport';
import { FLEET_FIT_OPTIONS, fleetBounds } from '../../utils/fleetViewport';
import { SearchResultMarker } from './SearchResultMarker';
import { FleetOwnershipFilter } from '../Fleet/FleetOwnershipFilter';
import { FlightTrackNotice } from './FlightTrackNotice';
import { useSelectedDroneTrack } from '../../hooks/useSelectedDroneTrack';
import { RegisterDroneButton } from '../Fleet/RegisterDroneDialog';
import { matchesOwnershipFilter } from '../../store/slices/dronesSlice';
import { getBasemapThemeClassName } from '../../styles/themes';
import { WORLD_MAP_OPTIONS, WorldBasemap } from './WorldBasemap';
import { setPendingPin } from '../../store/slices/homeBaseSlice';
import 'leaflet/dist/leaflet.css';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

// ---------------------------------------------------------------------------
// MapThemeUpdater — forces re-render on theme change (existing pattern)
// ---------------------------------------------------------------------------
const MapThemeUpdater: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [isDarkMode, map]);
  return null;
};

// ---------------------------------------------------------------------------
// MapClickHandler — handles home-base placement clicks.
// Must live inside MapContainer to use useMapEvents.
// ---------------------------------------------------------------------------
const MapClickHandler: React.FC = () => {
  const dispatch = useDispatch();
  const placementMode = useSelector(
    (state: RootState) => state.homeBase.placementMode
  );

  useMapEvents({
    click(e) {
      if (placementMode === 'placing') {
        dispatch(setPendingPin({ lat: e.latlng.lat, lng: e.latlng.lng }));
      }
    },
  });

  return null;
};

// ---------------------------------------------------------------------------
// PlacementBanner — floating instruction when in placement mode.
// ---------------------------------------------------------------------------
const PlacementBanner: React.FC = () => {
  const placementMode = useSelector(
    (state: RootState) => state.homeBase.placementMode
  );

  if (placementMode !== 'placing') {
    return null;
  }

  return (
    <Paper
      role="status"
      aria-live="polite"
      elevation={4}
      sx={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        px: 3,
        py: 1.5,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 2,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Typography variant="body2" fontWeight="bold">
        Click the map to place the home base. Press Escape to cancel.
      </Typography>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// DroneMap
// ---------------------------------------------------------------------------
export const DroneMap: React.FC = () => {
  const { isDarkMode } = useTheme();
  const drones = useSelector((state: RootState) => state.drones.drones);
  const selectedDroneId = useSelector(
    (state: RootState) => state.drones.selectedDroneId
  );
  const ownershipFilter = useSelector(
    (state: RootState) => state.drones.ownershipFilter
  );
  const homeBase = useSelector((state: RootState) => state.homeBase.homeBase);
  const shortestPath = useSelector(
    (state: RootState) => state.homeBase.shortestPath
  );
  const placementMode = useSelector(
    (state: RootState) => state.homeBase.placementMode
  );
  const mapRef = useRef<L.Map | null>(null);

  // Where the selected drone has flown (#581). The last acceptance criterion of
  // epic #380: history attributed to the owning pilot, drawn on the map they
  // already use rather than in a screen of its own.
  const {
    track: flightTrack,
    status: flightTrackStatus,
    error: flightTrackError,
  } = useSelectedDroneTrack();

  // The drones this view is showing (#385). Filtering here rather than inside
  // FleetClusterLayer keeps the clustering component about crowding and this one
  // about ownership, and lets the empty state below ask the same question the
  // markers answer.
  const visibleDrones = useMemo(
    () =>
      Object.values(drones).filter((drone) =>
        matchesOwnershipFilter(drone, ownershipFilter)
      ),
    [drones, ownershipFilter]
  );

  // Drones the map cannot place: no reported position, so no coordinates
  // (#508). They are counted rather than silently dropped — an aircraft missing
  // from the dashboard with nothing said about it is the failure mode this
  // system cares about most, and it is the same reasoning that keeps unclaimed
  // drones visible in #382 instead of scoping them away.
  const unplaceableCount = useMemo(
    () => visibleDrones.filter((drone) => !drone.position).length,
    [visibleDrones]
  );

  // Drones the map can place but is not showing, because they are outside the
  // viewport (#589). "Not shown" used to mean only the line above, so a fleet
  // of five could draw zero markers while the screen explained three of them.
  // Reported from inside the map, which is the only place the viewport exists.
  const [offScreenCount, setOffScreenCount] = useState(0);

  // Where this map opens: the remembered viewport if the operator has moved a
  // map before, otherwise the default (#441).
  const initialView = useInitialMapView();

  // Real position context beats the device location, so only offer to center
  // on the operator when the map has nothing of its own to show (issue #401).
  // A remembered viewport counts: re-centring on the device would undo the
  // restore the operator just came back for.
  //
  // Placeable drones, not any drones (#589). `Object.keys(drones).length > 0`
  // read a fleet of position-less aircraft as position context, so the
  // device-location fallback was disabled by exactly the drones that had left
  // the map with nothing to centre on.
  const hasPositionContext =
    initialView.remembered ||
    homeBase !== null ||
    visibleDrones.some((drone) => drone.position);

  // Frame the map on the fleet, on request. Offered rather than done whenever
  // the fleet moves out of view: the operator who panned to Łódź on purpose
  // wants to stay there (#441), and a map that snaps back is the same defect
  // pointing the other way.
  const showWholeFleet = useCallback(() => {
    const bounds = fleetBounds(visibleDrones);
    if (bounds !== null) {
      mapRef.current?.fitBounds(bounds, FLEET_FIT_OPTIONS);
    }
  }, [visibleDrones]);

  return (
    <Box
      // The dark-basemap filter lives here, not on the tile layer: a layer's
      // className is write-once, so it used to survive the switch to light mode
      // and blank the map (#440).
      className={getBasemapThemeClassName(isDarkMode)}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        // Crosshair cursor during placement mode — visual affordance
        cursor: placementMode === 'placing' ? 'crosshair' : undefined,
      }}
    >
      {/* Floating instruction banner — outside MapContainer so MUI Paper renders */}
      <PlacementBanner />

      {/* One column for the top-right overlays (#518).

          These were three independently-positioned elements — the search at
          top 12, the ownership filter at top 16, the empty-state notice at
          top 72 — all at zIndex 1000, each guessing how tall the others were.
          The filter landed on top of the search and hid it. A flex column has
          no offsets to get wrong, and adding a fourth overlay no longer means
          measuring the other three. */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
          // Leave the map usable: the column is only as wide as its widest
          // child, and clicks pass through the gaps between them.
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {/* Register a drone, real or simulated, without a terminal (#470, #389).

            One control for both kinds since #389: an enrol button here and a
            virtual-drone button in the opposite corner read as one modern and
            one legacy rather than as two different things (#525).

            Here rather than at top-left (#521): that corner belongs to
            Leaflet's zoom buttons, and this button was drawn straight over
            them. App actions go in the app's column; the map keeps its own
            controls in the corner every map puts them. */}
        <RegisterDroneButton />

        {/* Jump-to-location search, outside MapContainer so MUI Paper renders. */}
        <MapSearchField mapRef={mapRef} />

        {/* Whose drones this view is showing (#385). */}
        <FleetOwnershipFilter />

        {/* Why there is no flown track, when there is none (#581). */}
        {selectedDroneId && (
          <FlightTrackNotice
            status={flightTrackStatus}
            error={flightTrackError}
          />
        )}

        {/* The filter can select nothing. Say which filter, and that the fleet
            is not empty — a blank map otherwise reads as "no drones at all". */}
        {Object.keys(drones).length > 0 && visibleDrones.length === 0 && (
          <Paper
            role="status"
            aria-live="polite"
            elevation={3}
            sx={{ px: 2, py: 1, maxWidth: 260 }}
          >
            <Typography variant="body2">
              {ownershipFilter === 'mine'
                ? 'No drones are registered to you yet. Enrol one, or switch to All to see the unclaimed aircraft.'
                : 'Every drone in view has an owner. Switch to All to see the whole fleet.'}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Every drone this map is not showing, and why (#508, #589).

          The arithmetic has to close: markers in view + off-screen + never
          reported = the fleet. It did not, which is how five drones drew zero
          markers under a notice covering three. Both reasons are named here so
          that no aircraft goes missing from the dashboard unremarked —
          off-screen is as missing as position-less, and was the half nobody
          counted. */}
      {(unplaceableCount > 0 || offScreenCount > 0) && (
        <Paper
          role="status"
          aria-live="polite"
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 1000,
            px: 2,
            py: 1,
            maxWidth: 280,
          }}
        >
          <Stack spacing={1} alignItems="flex-start">
            {offScreenCount > 0 && (
              <Typography variant="body2">
                {offScreenCount === 1
                  ? '1 drone is outside this view.'
                  : `${offScreenCount} drones are outside this view.`}
              </Typography>
            )}
            {unplaceableCount > 0 && (
              <Typography variant="body2">
                {unplaceableCount === 1
                  ? '1 drone has never reported a position and is not shown on the map.'
                  : `${unplaceableCount} drones have never reported a position and are not shown on the map.`}
              </Typography>
            )}
            {offScreenCount > 0 && (
              <Button size="small" onClick={showWholeFleet}>
                Show whole fleet
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <MapContainer
        center={initialView.center}
        zoom={initialView.zoom}
        style={{ width: '100%', height: '100%' }}
        ref={mapRef}
        {...WORLD_MAP_OPTIONS}
      >
        <MapThemeUpdater isDarkMode={isDarkMode} />
        <MapViewMemory />

        {/* What the viewport is and is not showing of the fleet (#589).
            Inside the map because the viewport only exists here; it frames the
            map on the fleet when there is no remembered view to respect. */}
        <FleetViewport
          drones={visibleDrones}
          fitToFleet={!initialView.remembered}
          onOffScreenChange={setOffScreenCount}
        />

        {/* Where a location search landed (#523). */}
        <SearchResultMarker />
        <MapClickHandler />
        <DeviceLocationCenter enabled={!hasPositionContext} />

        {/* One world, filling the map, at every size (#576). */}
        <WorldBasemap />

        {/* Drone markers and the selected drone's flown track, clustered when
            they crowd (#434, #581). */}
        <FleetClusterLayer
          drones={visibleDrones}
          tracks={flightTrack ? [flightTrack] : []}
        />

        {/* Home base marker for the selected drone */}
        {homeBase && selectedDroneId && (
          <HomeBaseMarker homeBase={homeBase} />
        )}

        {/* Shortest path overlay for the selected drone */}
        {shortestPath && selectedDroneId && (
          <ShortestPathLayer shortestPath={shortestPath} />
        )}
      </MapContainer>
    </Box>
  );
};
